<template>
    <main id="js-page-content" class="home-v2" role="main">
        <portal to="ariane">
            <div class="tbv2-page-title">{{ $t('menu.dashboard') }}</div>
        </portal>

        <header class="home-hero">
            <div class="home-hero-main">
                <h1 class="home-hero-title">{{ $t('menu.dashboard') }}</h1>
                <p class="home-hero-sub">
                    {{ $t('auth.hello') }} {{ userImpersonated ? userImpersonated.name : user.name }},
                    {{ canViewPosts
                        ? $t(`dashboard.welcome_period.${period}`)
                        : $t('dashboard.welcome_sub') }}
                </p>
            </div>
            <div
                v-if="canViewPosts"
                class="home-period"
                role="radiogroup"
                :aria-label="$t('dashboard.period.label') || 'Période'"
            >
                <button
                    v-for="opt in periodOptions"
                    :key="opt.value"
                    type="button"
                    class="home-period-btn"
                    :class="{ 'home-period-btn--active': period === opt.value }"
                    :aria-checked="period === opt.value ? 'true' : 'false'"
                    role="radio"
                    @click="setPeriod(opt.value)"
                >
                    {{ $t(opt.key) }}
                </button>
            </div>
        </header>

        <!--
            Tout le tableau de bord parle de signalements : sans `posts.view`, il
            n'y a rien à montrer. Plutôt qu'un écran de refus en guise de page
            d'accueil, on propose les sections réellement ouvertes au rôle — et
            surtout on ne déclenche aucun appel qui finirait en /403.
        -->
        <HomeShortcuts v-if="!canViewPosts" :shortcuts="navShortcuts" />

        <template v-else-if="posts">
        <HomeCardsGallery
            :myposts_new_this_week="myposts_new_this_week"
            :myposts_processing_this_week="myposts_processing_this_week"
            :myposts_done_this_week="myposts_done_this_week"
            :new_this_week="new_this_week"
            :processing_this_week="processing_this_week"
            :done_this_week="done_this_week"
            :myposts_previous_new="myposts_previous_new"
            :myposts_previous_processing="myposts_previous_processing"
            :myposts_previous_done="myposts_previous_done"
            :previous_new="previous_new"
            :previous_processing="previous_processing"
            :previous_done="previous_done"
            :period="period"
        />

        <!-- Stats priorite tickets ouverts -->
        <div class="dash-section">
            <div class="dash-card">
                <div class="dash-card-header">
                    <i class="fal fa-fire text-blue-500"></i>
                    <h2 class="text-lg leading-6 font-medium text-blue-500 mb-0">
                        {{ $t('menu.tickets.openedbypriority') }}
                    </h2>
                </div>
                <div class="dash-priority-grid">
                    <HomeOpenedTicketsStat
                        :title="$t('post.page.priority.1')"
                        :total="nbTicketsObservation"
                        :width="(nbTicketsObservation / totalTickets) * 100"
                        to="/posts?priority=1"
                    />
                    <HomeOpenedTicketsStat
                        :title="$t('post.page.priority.2')"
                        :total="nbTicketsMineur"
                        :width="(nbTicketsMineur / totalTickets) * 100"
                        to="/posts?priority=2"
                    />
                    <HomeOpenedTicketsStat
                        :title="$t('post.page.priority.3')"
                        :total="nbTicketsModere"
                        :width="(nbTicketsModere / totalTickets) * 100"
                        to="/posts?priority=3"
                    />
                    <HomeOpenedTicketsStat
                        :title="$t('post.page.priority.4')"
                        :total="nbTicketsMajeur"
                        :width="(nbTicketsMajeur / totalTickets) * 100"
                        to="/posts?priority=4"
                    />
                    <HomeOpenedTicketsStat
                        :title="$t('post.page.priority.0')"
                        :total="nbTicketsSans"
                        :width="(nbTicketsSans / totalTickets) * 100"
                        to="/posts?priority=0"
                    />
                </div>
            </div>
        </div>

        <!-- Mes tickets ouverts -->
        <div v-if="isLoad && !loadingStatus" class="dash-section">
            <div class="dash-card">
                <div class="dash-card-header">
                    <i class="fal fa-ticket text-blue-500"></i>
                    <h2 class="text-lg leading-6 font-medium text-blue-500 mb-0">
                        {{ $t('menu.tickets.allmyticketsopen') }}
                    </h2>
                </div>
                <div class="dash-card-body">
                    <PostsTable :filterMyOpenedTickets="myOpenedTicketsFilter" />
                    <router-link to="/posts?responsability=operator" class="dash-btn">
                        <i class="fal fa-arrow-right text-white"></i>
                        {{ $t('menu.tickets.allmyticketsopen') }}
                    </router-link>
                </div>
            </div>
        </div>

        <!-- Mes tickets suivis -->
        <div v-if="isLoad && !loadingStatus" class="dash-section">
            <div class="dash-card">
                <div class="dash-card-header">
                    <i class="fal fa-eye text-blue-500"></i>
                    <h2 class="text-lg leading-6 font-medium text-blue-500 mb-0">
                        {{ $t('menu.tickets.allmyticketstracked') }}
                    </h2>
                </div>
                <div class="dash-card-body">
                    <PostsTable :filterMyTrackedTickets="myTrackedTicketsFilter" />
                    <router-link to="/posts?responsability=informed" class="dash-btn">
                        <i class="fal fa-arrow-right text-white"></i>
                        {{ $t('menu.tickets.allmyticketstracked') }}
                    </router-link>
                </div>
            </div>
        </div>

        <!-- Cartographie -->
        <div class="dash-section">
            <div class="dash-card">
                <div class="dash-card-header">
                    <i class="fal fa-map-marker-alt text-blue-500"></i>
                    <h2 class="text-lg leading-6 font-medium text-blue-500 mb-0">
                        {{ $t('menu.tickets.location') }}
                    </h2>
                </div>
                <div class="dash-map-tabs">
                    <button
                        class="dash-map-tab"
                        :class="{ 'dash-map-tab--active': localisationTab === 0 }"
                        @click="localisationTab = 0"
                    >
                        <i class="fal fa-user"></i>
                        {{ $t('menu.tickets.mylastopentickets') }}
                    </button>
                    <button
                        class="dash-map-tab"
                        :class="{ 'dash-map-tab--active': localisationTab === 1 }"
                        @click="localisationTab = 1"
                    >
                        <i class="fal fa-users"></i>
                        {{ $t('menu.tickets.alllastopentickets') }}
                    </button>
                </div>
                <MapDashboard :posts="postsWithLocation" :key="'map-' + localisationTab + '-' + postsWithLocation.length" />
            </div>
        </div>
        </template>
    </main>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import { mapActions, mapState, mapGetters } from 'vuex'
import HomeCardsGallery from '@/components/HomeCardsGallery'
import MapDashboard from '@/components/Model/Map/MapDashboard'
import PostsTable from '@/components/Model/Post/PostsTable'
import HomeOpenedTicketsStat from '../../components/HomeOpenedTicketsStat'
import HomeShortcuts from '@/components/Home/HomeShortcuts'
import { allowedNavSections } from '@/config/navigation'

export default {
    name: 'Home',
    components: {
        MapDashboard,
        HomeCardsGallery,
        PostsTable,
        HomeOpenedTicketsStat,
        HomeShortcuts
    },
    data: function () {
        return {
            myposts_new_this_week: 0,
            myposts_processing_this_week: 0,
            myposts_done_this_week: 0,
            new_this_week: 0,
            processing_this_week: 0,
            done_this_week: 0,
            myposts_previous_new: 0,
            myposts_previous_processing: 0,
            myposts_previous_done: 0,
            previous_new: 0,
            previous_processing: 0,
            previous_done: 0,
            postsWithLocation: [],
            nbTicketsObservation: 0,
            nbTicketsMineur: 0,
            nbTicketsModere: 0,
            nbTicketsMajeur: 0,
            nbTicketsSans: 0,
            isLoad: false,
            loadingStatus: true,
            localisationTab: 0,
            priorityDashboard: [],
            postpriority: [],
            period: 'today',
            periodOptions: [
                { value: 'today', key: 'dashboard.period.today' },
                { value: '7days', key: 'dashboard.period.7days' },
                { value: '30days', key: 'dashboard.period.30days' }
            ]
        }
    },
    created: function () {
        if (!this.canViewPosts) {
            this.isLoad = true
            this.loadingStatus = false
            return
        }

        const userId = this.userImpersonated
            ? this.userImpersonated.id
            : this.user.id

        this.loadDashboardStats()
        this.config.include = 'answers,medias,form'
        this.config.filters = {
            status_id: '1,2,3',
            with_operator: userId,
            with_location: ''
        }
        this.loadPostsLocation()
        // Les drapeaux doivent retomber même en erreur, sinon les cartes
        // restent bloquées sur leur état de chargement.
        this.getStatus({ silence403: true })
            .catch(() => {})
            .finally(() => {
                this.loadingStatus = false
            })
    },
    watch: {
        localisationTab: function (tab) {
            if (!this.canViewPosts) return
            const userId = this.userImpersonated
                ? this.userImpersonated.id
                : this.user.id
            this.config.filters = {
                status_id: '1,2,3',
                with_location: '',
                ...(tab === 0 ? { with_operator: userId } : {})
            }
            this.loadPostsLocation()
        }
    },
    computed: {
        permissions() {
            return this.$store.state.auth.permissions
                || JSON.parse(localStorage.getItem('permissions') || '[]')
        },
        canViewPosts() {
            return this.permissions.includes('posts.view')
        },
        isSuperAdmin() {
            const role = this.$store.state.auth.role
                || JSON.parse(localStorage.getItem('role') || 'null')
            const roleName = role && typeof role === 'object' ? role.name : role
            return roleName === 'super-admin'
        },
        /** Sections ouvertes au rôle, hors tableau de bord lui-même. */
        navShortcuts() {
            return allowedNavSections(
                { permissions: this.permissions, isSuperAdmin: this.isSuperAdmin },
                { exclude: ['/'] }
            )
        },
        ...mapState('post', {
            posts: (state) => state.postsDashboard,
            total: (state) => state.total,
            myPosts: (state) => state.myPosts
        }),
        ...mapState('auth', {
            user: (state) => state.user
        }),
        ...mapState('admin', {
            userImpersonated: (state) => state.userImpersonated
        }),
        config: {
            get() {
                return this.$store.state.post.myPostsConfig
            },
            set(value) {
                this.$store.commit('post/setmyPostsConfig', value)
            }
        },
        ...mapGetters('post', ['getPostsWithLocation']),
        myOpenedTicketsFilter() {
            const userId = this.userImpersonated ? this.userImpersonated.id : this.user.id
            return {
                status_id: '1,2,3',
                with_operator: userId
            }
        },
        myTrackedTicketsFilter() {
            const userId = this.userImpersonated
                ? this.userImpersonated.id
                : this.user.id
            return {
                with_user_informed: userId
            }
        },
        totalTickets: function () {
            return (
                this.nbTicketsObservation +
                this.nbTicketsMineur +
                this.nbTicketsModere +
                this.nbTicketsMajeur +
                this.nbTicketsSans
            )
        }
    },
    methods: {
        ...mapActions('post', [
            'getMyNeWPostsDashboard',
            'getPostsDashboard',
            'getPostsLocation'
        ]),
        ...mapActions('status', ['getStatus']),
        resetDashboardCounters() {
            this.myposts_new_this_week = 0
            this.myposts_processing_this_week = 0
            this.myposts_done_this_week = 0
            this.new_this_week = 0
            this.processing_this_week = 0
            this.done_this_week = 0
            this.myposts_previous_new = 0
            this.myposts_previous_processing = 0
            this.myposts_previous_done = 0
            this.previous_new = 0
            this.previous_processing = 0
            this.previous_done = 0
            this.nbTicketsObservation = 0
            this.nbTicketsMineur = 0
            this.nbTicketsModere = 0
            this.nbTicketsMajeur = 0
            this.nbTicketsSans = 0
        },
        setPeriod(value) {
            if (this.period === value) return
            this.period = value
            this.loadDashboardStats()
        },
        loadPostsLocation() {
            return this.getPostsLocation({
                filters: this.config,
                axiosConfig: { silence403: true }
            })
                .then((response) => {
                    this.postsWithLocation = response.data.posts.data.filter(
                        (post) => post.location
                    )
                })
                .catch(() => {
                    this.postsWithLocation = []
                })
                .finally(() => {
                    this.isLoad = true
                })
        },
        loadDashboardStats() {
            if (!this.canViewPosts) return
            this.resetDashboardCounters()
            const userId = this.userImpersonated
                ? this.userImpersonated.id
                : this.user.id

            this.getMyNeWPostsDashboard({
                userId,
                period: this.period,
                axiosConfig: { silence403: true }
            }).then((postDashboards) => {
                postDashboards.priority_dashboard.forEach((priority) => {
                    switch (priority.priority) {
                        case 0: this.nbTicketsSans = priority.total; break
                        case 1: this.nbTicketsObservation = priority.total; break
                        case 2: this.nbTicketsMineur = priority.total; break
                        case 3: this.nbTicketsModere = priority.total; break
                        case 4: this.nbTicketsMajeur = priority.total; break
                        default: break
                    }
                })
                postDashboards.status_dashboard.forEach((status) => {
                    if (!status.status) return
                    const prev = status.previous_total || 0
                    if (status.status.final === false) {
                        this.myposts_processing_this_week += status.total
                        this.myposts_previous_processing += prev
                        if (status.status.default === true) {
                            this.myposts_new_this_week += status.total
                            this.myposts_previous_new += prev
                        }
                    } else {
                        this.myposts_done_this_week += status.total
                        this.myposts_previous_done += prev
                    }
                })
            }).catch(() => {})

            this.getPostsDashboard({
                period: this.period,
                axiosConfig: { silence403: true }
            }).then((postDashboards) => {
                postDashboards.status_dashboard.forEach((status) => {
                    if (!status.status) return
                    const prev = status.previous_total || 0
                    if (status.status.final === false) {
                        this.processing_this_week += status.total
                        this.previous_processing += prev
                        if (status.status.default === true) {
                            this.new_this_week += status.total
                            this.previous_new += prev
                        }
                    } else {
                        this.done_this_week += status.total
                        this.previous_done += prev
                    }
                })
            }).catch(() => {})
        }
    },
    mounted() {}
}
</script>

<style scoped>
.home-v2 {
    width: 100%;
    padding: 36px 32px 64px;
    font-family: var(--v2-font-sans);
    color: var(--v2-text-2);
}

.home-hero {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: var(--v2-space-4);
    margin-bottom: var(--v2-space-6);
}

.home-hero-main {
    flex: 1;
    min-width: 0;
}

.home-period {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding: 3px;
    background: var(--v2-surface-1);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    flex-shrink: 0;
}

.home-period-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 28px;
    padding: 0 14px;
    border: none;
    background: transparent;
    color: var(--v2-text-3);
    font-family: inherit;
    font-size: 0.78rem;
    font-weight: var(--v2-font-weight-medium);
    line-height: 1;
    cursor: pointer;
    border-radius: var(--v2-radius-sm);
    white-space: nowrap;
    transition: background var(--v2-transition-fast), color var(--v2-transition-fast);
}

.home-period-btn:hover {
    color: var(--v2-text-1);
    background: var(--v2-surface-2);
}

.home-period-btn--active {
    background: var(--v2-surface-0);
    color: var(--v2-text-1);
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
}

.home-hero-title {
    margin: 0 0 6px;
    font-size: 1.75rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
    letter-spacing: -0.02em;
    line-height: 1.15;
}

.home-hero-sub {
    margin: 0;
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-3);
    line-height: 1.5;
}

.tbv2-page-title {
    font-size: 0.95rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    letter-spacing: -0.005em;
}

@media (max-width: 900px) {
    .home-v2 {
        padding: 24px 18px 32px;
    }

    .home-hero {
        flex-direction: column;
        align-items: stretch;
    }

    .home-hero-title {
        font-size: 1.25rem;
    }

    .home-period {
        align-self: flex-start;
    }
}

.dash-section {
    margin-top: 24px;
}

.dash-card {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.dash-card-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 24px;
    border-bottom: 1px solid #f1f5f9;
}

.dash-card-header > i {
    font-size: 1rem;
    flex-shrink: 0;
}

.dash-card-body {
    padding: 20px 24px;
}

.dash-priority-grid {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 16px;
    padding: 20px 24px;
}

@media (max-width: 1200px) {
    .dash-priority-grid {
        grid-template-columns: repeat(3, 1fr);
    }
}

@media (max-width: 768px) {
    .dash-priority-grid {
        grid-template-columns: 1fr;
    }
}

.dash-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 9px 20px;
    border-radius: 10px;
    background: #0891B2;
    color: #fff !important;
    font-size: 0.82rem;
    font-weight: 600;
    text-decoration: none !important;
    margin-top: 16px;
    transition: all 0.2s;
}

.dash-btn:hover {
    background: #0e7490;
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(8, 145, 178, 0.3);
}

.dash-btn i {
    color: #fff !important;
}

.dash-map-tabs {
    display: flex;
    gap: 6px;
    padding: 16px 24px;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
}

.dash-map-tab {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 8px 18px;
    border: 1.5px solid #e2e8f0;
    border-radius: 9px;
    font-size: 0.8rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    background: #fff;
    color: #64748b;
}

.dash-map-tab i {
    font-size: 0.75rem;
}

.dash-map-tab:hover {
    border-color: #0891B2;
    color: #0891B2;
    background: rgba(8, 145, 178, 0.04);
}

.dash-map-tab--active {
    background: rgba(8, 145, 178, 0.08);
    color: #0891B2;
    border-color: #0891B2;
}
</style>

<style>
.vue-portal-target {
    display: flex;
    align-items: center;
}
</style>
